import 'component/adaptive-video'

class HHVideoHero extends HTMLElement {
  constructor() {
    super()

    this.onClickAudioButton = this.onClickAudioButton.bind(this)
    this.onClickMotionButton = this.onClickMotionButton.bind(this)
    this.onVideoPlay = () => this.updateMotionButton(false)
    this.onVideoPause = () => this.updateMotionButton(true)
    this.onVideoVolumeChange = this.updateAudioButton.bind(this)
  }

  connectedCallback() {
    this.audioButton?.addEventListener('click', this.onClickAudioButton)
    this.motionButton?.addEventListener('click', this.onClickMotionButton)

    this.linkOverlays.forEach((link) =>
      link.addEventListener('click', this.onOverlayLinkClick.bind(this))
    )

    this.videoElement?.updateComplete.then(() => {
      this.heroVideo?.addEventListener?.('volumechange', this.onVideoVolumeChange)
      this.updateAudioButton()
      this.heroVideo?.addEventListener?.('play', this.onVideoPlay)
      this.heroVideo?.addEventListener?.('pause', this.onVideoPause)
      this.updateMotionButton(this.heroVideo?.paused ?? true)
    })
  }

  disconnectedCallback() {
    this.heroVideo?.removeEventListener?.('volumechange', this.onVideoVolumeChange)
    this.audioButton?.removeEventListener('click', this.onClickAudioButton)
    this.motionButton?.removeEventListener('click', this.onClickMotionButton)
    this.heroVideo?.removeEventListener?.('play', this.onVideoPlay)
    this.heroVideo?.removeEventListener?.('pause', this.onVideoPause)
  }

  onClickAudioButton() {
    this.videoElement?.handleToggleAudio()
    this.updateAudioButton()
  }

  updateAudioButton() {
    const video = this.heroVideo
    if (!this.audioButton || typeof video?.muted !== 'boolean') return

    const targetMode = video.muted ? 'mute' : 'play'
    this.audioButton.setAttribute('data-mode', targetMode)
    if (this.audioButton.hasAttribute('aria-pressed')) {
      this.audioButton.setAttribute('aria-pressed', String(video.muted))
    }
    this.audioIcons.forEach((icon) => {
      const mode = icon.getAttribute('data-mode')
      if (mode == targetMode) {
        icon.classList.remove('hidden')
      } else {
        icon.classList.add('hidden')
      }
    })
  }

  onOverlayLinkClick(event) {
    const video = this.heroVideo
    // First tap on a linked banner starts the video instead of navigating.
    // Once it's playing, the tap falls through and the link works normally.
    if (video && video.paused && !this._pausedByUser && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      event.preventDefault()
      video.play()
    }
  }

  onClickMotionButton() {
    const video = this.heroVideo
    if (!video) return

    this._pausedByUser = !video.paused
    if (video.paused) {
      this.videoElement.handlePlayClick()
    } else {
      video.pause()
    }
  }

  updateMotionButton(paused) {
    if (!paused && this._pausedByUser) {
      this.heroVideo?.pause()
      return
    }
    if (!this.motionButton) return

    this.motionButton.dataset.paused = String(paused)
    if (this.motionButton.hasAttribute('aria-pressed')) {
      this.motionButton.setAttribute('aria-pressed', String(paused))
    }
    this.motionLabel.textContent = paused
      ? this.motionButton.dataset.playLabel
      : this.motionButton.dataset.pauseLabel
  }

  get videoElement() {
    return this.querySelector('hh-adaptive-video')
  }

  get heroVideo() {
    return this.videoElement?.videoElement ?? null
  }

  get linkOverlays() {
    return this.querySelectorAll('.video-hero__full-link, a.hero-overlay')
  }

  get audioButton() {
    return this.querySelector('.js-audio-button')
  }

  get audioIcons() {
    return this.querySelectorAll('.js-audio-icon')
  }

  get motionButton() {
    return this.querySelector('.js-video-motion-toggle')
  }

  get motionLabel() {
    return this.motionButton?.querySelector('.js-video-motion-label')
  }
}

if (!customElements.get('hh-video-hero')) {
  customElements.define('hh-video-hero', HHVideoHero)
}
